import { useEffect } from "react";

import type { HeadingTagType } from "@lexical/rich-text";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_DOWN_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from "lexical";

import { useToolbarStateContext } from "../../toolbar-state-provider";
import {
  formatBulletList,
  formatHeading,
  formatNumberedList,
  formatParagraph,
} from "../../utils";
import {
  isFormatBulletList,
  isFormatHeading,
  isFormatNumberedList,
  isFormatParagraph,
  isIndent,
  isModifierMatch,
  isOutdent,
  isStrikeThrough,
} from "./shortcuts";

export function ShortcutsPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const toolbarState = useToolbarStateContext();

  useEffect(() => {
    const keyboardShortcutsHandler = (event: KeyboardEvent) => {
      if (isModifierMatch(event, {})) {
        return false;
      } else if (isFormatParagraph(event)) {
        formatParagraph(editor);
      } else if (isFormatHeading(event)) {
        const { code } = event;
        const headingSize = `h${code[code.length - 1]}` as HeadingTagType;
        formatHeading(editor, toolbarState.blockType, headingSize);
      } else if (isFormatBulletList(event)) {
        formatBulletList(editor, toolbarState.blockType);
      } else if (isFormatNumberedList(event)) {
        formatNumberedList(editor, toolbarState.blockType);
      } else if (isStrikeThrough(event)) {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
      } else if (isIndent(event)) {
        editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
      } else if (isOutdent(event)) {
        editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
      } else {
        return false;
      }
      event.preventDefault();
      return true;
    };

    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      keyboardShortcutsHandler,
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, toolbarState.blockType]);

  return null;
}
